import { useEffect, useState } from 'react';
import { can, faNum as fa, type SetupItem, type SetupStatus } from '@kia/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import type { View } from '../nav/navModel';
import { Button, Chip, Modal, useToast } from '../ui';

/**
 * Onboarding / setup gate (§3a) — the proactive "lead the user" surface. On login (Admin only), if a Tier-1
 * input that makes COGS·price·margin real is still missing, a checklist modal opens with a deep-link to the
 * editor that fixes each one; the owner can «بعداً» (snooze a Tier-1 item → it becomes a persistent chip
 * instead of a nag) or dismiss for the session. A slim "setup remaining" chip reopens it until everything is
 * set. Reads the single authority `GET /api/setup/status`; writes only snoozes. Never shown once fully set up.
 */
export function OnboardingGate({ onNavigate }: { onNavigate: (v: View, tab?: string) => void }) {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user ? can(user.role, 'config.write') : false;
  const [status, setStatus] = useState<SetupStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    void api<SetupStatus>('/api/setup/status').then((r) => {
      if (!r.ok) return;
      const st = r.body as SetupStatus;
      setStatus(st);
      if (st.blocking > 0 && !dismissed) setOpen(true); // auto-open while Tier-1 items still block
    });
  }, [isAdmin, dismissed]);

  if (!isAdmin || !status) return null;
  const remaining = status.totalCount - status.doneCount;
  if (remaining === 0) return null; // fully configured — get out of the way

  // Tier-weight the progress: Tier-1 (ضروری) is what makes COGS/price/margin real, so the bar tracks IT — not a
  // blended count that could read "80%" while a Tier-1 blocker is still missing. Tier-2 (اسناد) shown alongside.
  const t1 = status.items.filter((i) => i.tier === 1);
  const t2 = status.items.filter((i) => i.tier === 2);
  const t1Done = t1.filter((i) => i.done).length;
  const t2Done = t2.filter((i) => i.done).length;
  const t1Pct = t1.length ? Math.round((t1Done / t1.length) * 100) : 100;

  const snooze = async (itemKey: string) => {
    const r = await api<SetupStatus>('/api/setup/snooze', {
      method: 'POST',
      body: JSON.stringify({ itemKey, days: 14 }),
    });
    if (r.ok) {
      setStatus(r.body as SetupStatus);
      toast('۱۴ روز به تعویق افتاد — در نوار پایین یادآوری می‌ماند');
    } else toast('خطا در به تعویق انداختن', 'error');
  };
  const go = (view: string, tab?: string) => {
    setOpen(false);
    onNavigate(view as View, tab);
  };

  const Row = ({ i }: { i: SetupItem }) => (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '10px 0',
        borderTop: '1px solid var(--line)',
      }}
    >
      <span style={{ fontSize: '1.1em', lineHeight: 1.6 }}>
        {i.done ? '✓' : i.snoozedUntil ? '⏳' : i.tier === 1 ? '●' : '○'}
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <b>{i.label}</b>
          {i.done ? (
            <Chip tone="success">انجام شد</Chip>
          ) : i.snoozedUntil ? (
            <Chip tone="warn">تا {i.snoozedUntil}</Chip>
          ) : i.tier === 1 ? (
            <Chip tone="warn">لازم</Chip>
          ) : (
            <Chip>یادآوری</Chip>
          )}
        </div>
        <p style={{ margin: '4px 0 6px', color: 'var(--ink-3)', lineHeight: 1.8 }}>{i.detail}</p>
        {!i.done ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => go(i.ctaView, i.ctaTab)}>{i.ctaLabel}</Button>
            {i.tier === 1 && !i.snoozedUntil ? (
              <Button variant="secondary" onClick={() => void snooze(i.key)}>
                بعداً
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            // inset-inline-END = physical LEFT in RTL = the CONTENT side, clear of the nav sidebar
            // (inline-start) — the same rule the smart-fab follows. Lifted above the ✨ FAB so they don't
            // collide. The raised drawer backdrop (z-65) covers it when the mobile drawer is open.
            bottom: 88,
            insetInlineEnd: 16,
            zIndex: 40,
            // Honest urgency: amber while any Tier-1 item is unfinished — even if SNOOZED. Snooze suppresses the
            // modal auto-open (the nag), never the "not ready" signal; a snoozed blocker must not read as safe.
            background: !status.tier1Ready ? 'var(--amber)' : 'var(--card)',
            color: !status.tier1Ready ? '#000' : 'var(--ink)',
            border: '1px solid var(--line)',
            borderRadius: 999,
            padding: '8px 14px',
            cursor: 'pointer',
            boxShadow: 'var(--shadow, 0 2px 8px rgba(0,0,0,.15))',
            fontFamily: 'inherit',
          }}
        >
          راه‌اندازی — {fa(remaining)} مورد مانده
        </button>
      ) : null}

      {open ? (
        <Modal
          title="راه‌اندازی اولیهٔ سامانه"
          onClose={() => {
            setOpen(false);
            setDismissed(true);
          }}
          actions={
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                setDismissed(true);
              }}
            >
              فعلاً می‌بندم
            </Button>
          }
        >
          <p style={{ margin: '0 0 8px', color: 'var(--ink-2)', lineHeight: 1.9 }}>
            این‌ها ورودی‌هایی هستند که بدون‌شان بهای تمام‌شده، قیمت و حاشیهٔ سود درست محاسبه
            نمی‌شوند. هر مورد را همین‌جا باز کنید و تنظیم نمایید — یا «بعداً» بزنید تا به‌جای هشدار،
            یادآوری بماند.
          </p>
          <div
            style={{
              height: 8,
              background: 'var(--line)',
              borderRadius: 999,
              overflow: 'hidden',
              margin: '0 0 4px',
            }}
          >
            <div
              style={{
                width: `${t1Pct}%`,
                height: '100%',
                background: status.tier1Ready ? 'var(--green)' : 'var(--amber)',
              }}
            />
          </div>
          <p style={{ margin: '0 0 10px', color: 'var(--ink-3)', fontSize: '.9em' }}>
            ضروری برای بهایابی: {fa(t1Done)} از {fa(t1.length)} · برای صدور اسناد: {fa(t2Done)} از{' '}
            {fa(t2.length)}
          </p>

          <b style={{ color: 'var(--amber)' }}>ضروری — بدون این‌ها بهایابی نادرست است</b>
          {status.items
            .filter((i) => i.tier === 1)
            .map((i) => (
              <Row key={i.key} i={i} />
            ))}
          <b style={{ display: 'block', marginTop: 14 }}>برای صدور اسناد</b>
          {status.items
            .filter((i) => i.tier === 2)
            .map((i) => (
              <Row key={i.key} i={i} />
            ))}
        </Modal>
      ) : null}
    </>
  );
}
